import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtTimeCT } from "@/lib/api";
import { sportShort } from "@/lib/sport";
import { saveSnapshot, loadSnapshot } from "@/lib/offline";
import { CircleDot, CloudOff } from "lucide-react";

function TickerItem({ e }) {
  const live = e.status?.state === "in";
  const pre = e.status?.state === "pre";
  const slug = e.home_slug || e.away_slug;
  const inner = (
    <>
      <span className="rounded-sm bg-bcast/20 px-1.5 py-0.5 font-display text-xs tracking-widest text-bcast">{sportShort(e.league)}</span>
      <span className="font-score text-xs text-slate-300">
        {e.away?.abbr || e.away?.name} <span className="text-white">{e.away?.score ?? ""}</span>
        <span className="mx-1 text-slate-600">@</span>
        {e.home?.abbr || e.home?.name} <span className="text-white">{e.home?.score ?? ""}</span>
      </span>
      {live ? (
        <span className="inline-flex items-center gap-1 font-score text-[10px] text-liversed">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-liversed" />
          {e.status?.detail || "LIVE"}
        </span>
      ) : pre ? (
        <span className="font-score text-[10px] text-slate-500">{fmtTimeCT(e.date)} CT</span>
      ) : (
        <span className="font-score text-[10px] text-slate-500">FINAL</span>
      )}
      {e.odds?.details && <span className="font-score text-[10px] text-volt/80">{e.odds.details}</span>}
    </>
  );
  const cls = "mx-5 inline-flex items-center gap-2 whitespace-nowrap";
  if (slug) {
    return (
      <Link
        to={`/team/${slug}`}
        data-testid={`ticker-link-${slug}`}
        className={`${cls} rounded-sm px-1 transition-colors hover:bg-volt/10`}
        title="Jump to team page"
      >
        {inner}
      </Link>
    );
  }
  return <span className={cls}>{inner}</span>;
}

export default function Ticker() {
  const [events, setEvents] = useState([]);
  const [meta, setMeta] = useState(null);
  const [offline, setOffline] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const { data } = await api.get("/ticker");
        if (mounted) {
          setEvents(data.events || []);
          setMeta(data);
          setOffline(false);
          saveSnapshot("ticker", data.events || []);
        }
      } catch {
        if (!mounted) return;
        const snap = loadSnapshot("ticker");
        if (snap?.data?.length) {
          setEvents(snap.data);
          setOffline(true);
        }
      }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  const items = events.length ? events : [];

  return (
    <div className="border-b border-bline bg-surface/60" data-testid="live-ticker">
      <div className="marquee-paused relative flex items-center overflow-hidden">
        <div className="z-10 flex shrink-0 items-center gap-2 border-r border-bline bg-ink px-4 py-2">
          <CircleDot className="h-3.5 w-3.5 animate-pulse text-liversed" />
          <span className="font-display text-sm tracking-[0.2em] text-white">LIVE&nbsp;WIRE</span>
          {offline ? (
            <span data-testid="ticker-offline" className="inline-flex items-center gap-1 font-score text-[10px] text-gold">
              <CloudOff className="h-3 w-3" /> OFFLINE
            </span>
          ) : (
            <span className="hidden font-score text-[10px] text-slate-500 lg:inline">{meta?.window || "7AM–12AM CT"}</span>
          )}
        </div>
        {items.length > 0 ? (
          <div className="flex animate-marquee py-2" data-testid="live-ticker-marquee">
            {[...items, ...items].map((e, i) => <TickerItem key={`${e.id}-${i}`} e={e} />)}
          </div>
        ) : (
          <p className="px-4 py-2 font-score text-xs text-slate-500" data-testid="ticker-empty">
            No games in the 7AM–12AM CT window right now — the wire lights up when DFW plays.
          </p>
        )}
      </div>
    </div>
  );
}
